import type { ApiErrorBody } from '@grs-atlas/shared';
import { config } from '../config';
import { getAccessToken, getActiveAccount } from '../auth/msal';

export class ApiClientError extends Error {
  constructor(
    public status: number,
    public body: ApiErrorBody,
  ) {
    super(body.message);
    this.name = 'ApiClientError';
  }
}

async function authHeaders(): Promise<Record<string, string>> {
  if (config.devAuth) {
    return {
      'X-Dev-User': localStorage.getItem('atlas-dev-user') || 'dev@guidedreach.com',
      'X-Dev-Roles': localStorage.getItem('atlas-dev-roles') || 'Atlas.Admin',
    };
  }
  const account = getActiveAccount();
  if (!account) return {};
  const token = await getAccessToken(account);
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function apiFetch<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const headers = new Headers(init.headers);
  if (!headers.has('Content-Type') && init.body) {
    headers.set('Content-Type', 'application/json');
  }
  const auth = await authHeaders();
  for (const [k, v] of Object.entries(auth)) {
    headers.set(k, v);
  }

  const res = await fetch(`${config.apiBaseUrl}${path}`, { ...init, headers });
  if (!res.ok) {
    let body: ApiErrorBody;
    try {
      body = (await res.json()) as ApiErrorBody;
    } catch {
      body = { error: 'INTERNAL_ERROR', message: res.statusText };
    }
    throw new ApiClientError(res.status, body);
  }
  if (res.status === 204) return undefined as T;
  const contentType = res.headers.get('content-type') ?? '';
  if (contentType.includes('text/csv')) {
    return (await res.text()) as T;
  }
  return (await res.json()) as T;
}
